/**
 * Defines string constants representing feature, widget, settings, data category, and
 * relationship identifiers used throughout the Productivity Portal application for:
 *  - Routing / feature toggling
 *  - Local storage / persistence keys
 *  - API query or cache namespace segmentation
 *  - Component / widget registry lookup
 *
 * Each enum member maps to the canonical, kebab‑case (or lowercase) string key
 * expected by backend services, configuration providers, or UI composition logic.
 *
 * @remarks
 * Keep these values stable; changing a key is a breaking change that can invalidate
 * stored user preferences (e.g., hidden widgets, layout settings) and cached data.
 * When introducing a new constant:
 *  1. Prefer a descriptive, concise uppercase enum member.
 *  2. Use a kebab-case (or lowercase) string literal that matches existing naming style.
 *  3. Check for collisions with current backend / storage namespaces.
 *
 * @enum
 *
 * @property WIDGETS                Root key for widget collection or registry metadata.
 * @property ZSE                    Domain-specific feature flag or data group (context-dependent).
 * @property WIFI                   Wi-Fi usage / connectivity widget identifier.
 * @property MEETINGS               Calendar / meeting analytics widget key.
 * @property INVENTORY              Equipment or asset inventory feature key.
 * @property TIMESHEET_COMPLETENESS Timesheet completeness tracking / compliance widget key.
 * @property EMPLOYEE_CLIENTS       Mapping / list of clients associated with the current employee.
 * @property ABSENCES               Annual leave / absences overview widget key.
 * @property HOURS                  Hours distribution / allocation analytics widget key.
 * @property TEAM_REMOTE_WORK       Team-wide remote work overview / statistics.
 * @property REMOTE_WORK            Individual user's remote work view / stats.
 * @property TEAM_ABSENCES          Team absences aggregation widget key.
 * @property SPAN_HEROES            Recognition / awards (heroes) feature key.
 * @property CERTIFICATES           Professional certificates / qualifications widget key.
 * @property USER_TITLE             User hierarchy position / title identifier.
 * @property PRODUCTIVITY           Productivity metrics / scoring widget key.
 * @property HIDDEN_WIDGETS         Persistence key for user-hidden widget identifiers.
 * @property SETTINGS               General settings / preferences section key.
 * @property TEAMS                  Microsoft Teams activity / engagement analytics key.
 * @property MANAGERS               Upward reporting (managers) relationship data key.
 * @property SUBORDINATES           Downward reporting (subordinates) relationship data key.
 *
 * @see Hidden widget logic: HIDDEN_WIDGETS
 * @see Remote work analytics: TEAM_REMOTE_WORK, REMOTE_WORK
 * @see Absence tracking: ABSENCES, TEAM_ABSENCES
 */
export enum Constants {
  WIDGETS = 'widgets',
  ZSE = 'zse',
  WIFI = 'wifi',
  MEETINGS = 'meetings',
  INVENTORY = 'inventory',
  TIMESHEET_COMPLETENESS = 'timesheet-completness',
  EMPLOYEE_CLIENTS = 'clients',
  ABSENCES = 'annual-leave',
  HOURS = 'hours-distribution',
  TEAM_REMOTE_WORK = 'team-remote-work',
  REMOTE_WORK = 'my-remote-work',
  TEAM_ABSENCES = 'absences',
  SPAN_HEROES = 'heroes',
  CERTIFICATES = 'certificates',
  USER_TITLE = 'hierarchy-position-name',
  PRODUCTIVITY = 'productivity',
  HIDDEN_WIDGETS = 'hidden-widgets',
  SETTINGS = 'settings',
  TEAMS = 'microsoft-teams-activity',
  MANAGERS = 'managers',
  SUBORDINATES = 'subordinates',
  SIGNATURE_PUBLIC_KEY = 'signature-public-key',
  SIGNATURE = 'signature',
  LOCATIONS = 'locations',
  USER = 'user',
  NUMBERS = 'numbers',
  SUPPORT = 'support',
}
